import NotificationItem, { LeadingIcon } from 'components/notification/NotificationItem';
import { CLIENT_CONTENT, type ClientContent } from 'pages/notification/notificationService';
import type { Notification } from 'types/notification';

const TEAM_PURPLE = '#5914E5';

const REPORT_BUTTON = 'typography-headline-sm w-full rounded-full px-4 py-2 text-center';

/** 신고 관련 문구의 <span> 은 빨간색으로 읽힌다. */
function paintReportSpans(html: string): string {
  return html.replaceAll('<span>', '<span style="color:#F03439;">');
}

function buildContent(item: Notification, content: ClientContent): string {
  const text = brsc[content.contentKey];
  if (item.category === 'banreport') {
    return paintReportSpans(text.replace('$param', `<b>${item.reportNick ?? ''}</b>`));
  }
  if (item.category === 'reportsubmitted') {
    return paintReportSpans(text.replace('$param', TimeUtil.getFullDateTime(new Date(item.createdTime))));
  }
  return item.category.includes('report') ? paintReportSpans(text) : text;
}

/** 경고·이용제한 안내는 문구 대신 경고 상자와 금지 행위 목록을 편다. */
const UncomfortableNotice: React.FC<{ category: string }> = ({ category }) => {
  const warning = category === 'uncomfortableWarning';
  const notice = warning ? brsc.first_warning_notice : brsc.usage_restriction_notice;
  const title = warning ? brsc.first_warning_title : brsc.usage_restriction_title;

  return (
    <div className="flex flex-col">
      <div className="typography-headline-sm mb-4 rounded-md border border-solid border-[#F03439] bg-[#FFF0F0] p-4 text-[#F03439]">{notice}</div>
      <div className="typography-headline-sm py-4 text-[#F03439]">{title}</div>
      <div className="py-2">
        <div className="typography-headline-sm mb-3 text-base-txt-primary">{brsc.first_warning_warning}</div>
        {[brsc.first_warning_body_1, brsc.first_warning_body_2, brsc.first_warning_body_3, brsc.first_warning_body_4].map((line) => (
          <div className="flex gap-2" key={line}>
            <span>•</span>
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SystemItemProps {
  item: Notification;
  showUnreadHeader?: boolean;
  onLinkClick: (link: ClientContent['link']) => void;
  onCommunityRulesClick: () => void;
  onInquiryClick: () => void;
}

const SystemItem: React.FC<SystemItemProps> = ({ item, showUnreadHeader, onLinkClick, onCommunityRulesClick, onInquiryClick }) => {
  // 문구가 없는 category 는 notificationService 가 목록에서 빼므로 여기까지 오지 않는다.
  const content = CLIENT_CONTENT[item.category];
  if (!content) return null;

  const uncomfortable = item.category === 'uncomfortableWarning' || item.category === 'uncomfortableRestrict';
  const profileRejected = item.category === 'minorreport' || item.category === 'profilereport';

  const buttons = profileRejected ? (
    <>
      <div className={`${REPORT_BUTTON} bg-btn-bg-primary text-btn-txt-primary`} onClick={onCommunityRulesClick}>
        {brsc.profile_reject_noti_btn}
      </div>
      <div className={`${REPORT_BUTTON} mt-4 border border-solid border-btn-border-normal text-base-txt-disabled`} onClick={onInquiryClick}>
        {brsc.restrictions_contact}
      </div>
    </>
  ) : (
    content.buttonKey && (
      <div
        className={`typography-caption-head flex h-10 w-full items-center justify-center rounded-[5px] ${
          item.unread ? 'bg-btn-bg-secondary text-btn-txt-secondary' : 'bg-btn-bg-normal text-btn-txt-normal'
        }`}
        onClick={() => onLinkClick(content.link)}
      >
        {brsc[content.buttonKey]}
      </div>
    )
  );

  return (
    <NotificationItem
      unread={item.unread}
      showUnreadHeader={showUnreadHeader}
      createdTime={item.createdTime}
      leading={<LeadingIcon src="/imgweb/notification/bermuda_logo.png" background={TEAM_PURPLE} />}
      title={brsc[content.titleKey]}
      content={
        uncomfortable ? <UncomfortableNotice category={item.category} /> : <span dangerouslySetInnerHTML={{ __html: buildContent(item, content) }} />
      }
      bottom={
        <div className="mt-[10px] flex flex-col">
          {content.photoUrl && <img src={content.photoUrl} className="my-[10px] max-h-40 max-w-full rounded-md object-cover" alt="" />}
          {buttons}
        </div>
      }
    />
  );
};

export default SystemItem;
